import type { Temporal } from 'temporal-polyfill';
import type { useI18n } from 'vue-i18n';
import type { FormatLocale } from '#layers/base/types/locale';
import { isPastDateTime } from './dates';
import { formatRelativeTime } from './relative-time';

/**
 * Human-readable expiry label shared by the API key and pending-invite listings:
 * "Never expires" when there's no expiry, otherwise "Expires {relative}" / "Expired {relative}".
 */
export function formatExpiryLabel(
	expiresAt: Temporal.ZonedDateTime | null | undefined,
	i18n: ReturnType<typeof useI18n>,
	locale: FormatLocale,
): string {
	const { t } = i18n;

	if (!expiresAt) return t('expiry.never');

	// Expiry is about remaining runway: keep exact days rather than collapsing 715 of them into "in 1 year", and count
	// elapsed time below a day so a key that lapsed a minute ago does not read "Expired yesterday" just past midnight.
	const days = formatRelativeTime(expiresAt, i18n, { locale, granularity: 'minute', maxUnit: 'day' });

	return isPastDateTime(expiresAt)
		? t('expiry.expired', { days })
		: t('expiry.expires', { days });
}
